import { Button, Dialog, Text } from '@/components/DesignSystem';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { UserSyncApi } from '@/utilities/api/UserSyncApi';
import { UserSyncEndpoint } from '@api/endpoints/UserSyncEndpoint';
import { Alert, CircularProgress, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = { onClose: () => void, onStarted: () => void, open: boolean };

const Section = ({ labels, title }: { labels: string[], title: string }) => (
  <Stack gap={0.5}>
    <Text category="title-small">{title} ({labels.length})</Text>
    {labels.map((label) => (
      <Text breakWord key={label} sx={{ color: 'text.secondary' }}>{label}</Text>
    ))}
  </Stack>
);

/** Shows Align all's dry run before starting it in the background. */
const AlignAllDialog = ({ onClose, onStarted, open }: Props) => {
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const [plan, setPlan] = useState<UserSyncEndpoint.Align.Response | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    setPlan(null);
    setError(null);
    if (open) {
      UserSyncApi.align({ dryRun: true })
        .then(setPlan)
        .catch((err: Error) => setError(err.message));
    }
  }, [open]);

  const nothingToDo = plan !== null && plan.linked.length + plan.created.length === 0;

  const apply = async () => {
    setIsStarting(true);
    try {
      const result = await UserSyncApi.align({ dryRun: false });
      openSnackbar(t(($) => $.userSync.align.started, {
        created: result.created.length,
        linked: result.linked.length,
      }));
      onStarted();
    } catch (err) {
      openSnackbar((err as Error).message);
    } finally {
      setIsStarting(false);
      onClose();
    }
  };

  return (
    <Dialog
      acceptButton={(props) => (
        <Button
          {...props}
          disabled={!plan || nothingToDo}
          loading={isStarting}
          onClick={() => void apply()}
        >
          {t(($) => $.userSync.align.apply)}
        </Button>
      )}
      cancelButton={(props) => (
        <Button {...props} onClick={onClose}>{t(($) => $.userSync.align.cancel)}</Button>
      )}
      maxWidth="sm"
      onClose={onClose}
      open={open}
      testID="user-sync-align"
      title={t(($) => $.userSync.align.title)}
    >
      {!plan && !error && (
        <Stack alignItems="center" py={3}>
          <CircularProgress size={32} />
        </Stack>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {plan && (
        <Stack data-testid="align-plan" gap={2}>
          <Section labels={plan.linked} title={t(($) => $.userSync.align.link)} />
          <Section labels={plan.created} title={t(($) => $.userSync.align.create)} />
          <Section
            labels={plan.skipped.map(({ label, reason }) => `${label}: ${reason}`)}
            title={t(($) => $.userSync.align.skipped)}
          />
          {nothingToDo && (
            <Alert severity="success">{t(($) => $.userSync.align.nothingToDo)}</Alert>
          )}
        </Stack>
      )}
    </Dialog>
  );
};

export default AlignAllDialog;
